/**
 * Widgets/Form - Widget for coordinating multiple form inputs
 *
 * WIDGET: Coordinates multiple Input instances as a unified form. Unlike Module,
 * Widget augments existing markup and doesn't own rendering logic.
 *
 * MODULE VS WIDGET:
 * - Module: Owns templates and rendering
 * - Widget: Coordinates children, no rendering
 *
 * FEATURES:
 * - Multi-input coordination
 * - Unified validation
 * - Form submission handling
 * - Value serialization
 * - Submit button state management
 *
 * @class Widgets/Form
 * @extends Controller
 *
 * @example
 * // Create form widget
 * this.create({
 *     namespace: 'Widgets/Form',
 *     setup: {
 *         inputs: {
 *             email: { type: 'Inputs/Email' },
 *             password: { type: 'Inputs/Password' }
 *         }
 *     }
 * });
 */
define("Widgets/Form", [`Controller`, `module`], (Super, Self) => {
	// TODO: setup.focus<String|Boolean> param

	return Super.inherit({
		namespace: Self.id,
		requires: [`App`],
		defaults: {
			inputs: {}, // the set of inputs to initialize
			values: {}, // any values to assign to the inputs, once they're ready
			interceptEvents: false,
			toggleSubmit: false,
		},
		constructor(setup) {
			const { container, data } = this;
			let inputPromises;
			let inputToFocus;
			let theme;

			// if(this.isString(setup.theme, 1)) {
			// 	theme = setup.theme;
			// } else if(this.isElement(this.container)) {
			// 	theme = this.container.getAttribute('theme');
			// } else {
			// 	theme = null;
			// }

			data.toggleSubmit = setup.toggleSubmit;
			data.groups = {}; // a map of input name => group name | null

			// special validation, checks if one input should 'confirm' another, such as 'confirm password' => 'password'
			data.confirmations = {}; // a map of input name => input name | null

			if (container.nodeName === `FORM`) {
				const attributes = [`autocapitalize`, `autocomplete`, `autocorrect`];
				this.each(attributes, (attr) => container.setAttribute(attr, `off`));
			}

			inputPromises = this.map(setup.inputs, function (config, name) {
				var inputContainer = config.container,
					currentTheme;

				this.defaults(config, {
					group: null,
					setup: {},
					rules: {},
					validation: {},
					skipCallbacks: true,
					disable: false,
					readonly: false,
				});

				this.defaults(config.setup, {
					theme,
					show: true,
					transition: `none`,
					rules: {},
					validation: {},
				});

				this.assign(config.setup.rules, config.rules); // sugar ability, can skip nesting rules into setup
				this.assign(config.setup.validation, config.validation); // sugar ability, can skip nesting rules into setup

				// Pass through Dropdown (and similar) top-level setup flags that aren't part of rules/validation
				[`isIconAfterValue`, `hideValueOnOpen`, `listItemHasRadioButton`, `selectOnTab`].forEach((key) => {
					if (this.isDefined(config[key]) && this.isUndefined(config.setup[key])) {
						config.setup[key] = config[key];
					}
				});

				if (this.isString(config.setup.validation.confirm, 1)) {
					// this input 'confirms' another one
					this.data.confirmations[name] = config.setup.validation.confirm;
				} else {
					this.data.confirmations[name] = null;
				}

				if (!this.isElement(inputContainer)) {
					inputContainer = this.DOM.get(`[name="${name}"]`);
				}

				if (this.isElement(inputContainer)) {
					currentTheme = inputContainer.getAttribute(`theme`);
					if (!this.isString(currentTheme) && this.isString(theme)) {
						inputContainer.setAttribute(`theme`, theme);
					}
				}

				if (this.isDefined(config.value)) {
					// a value was passed with the input config
					setup.values[name] = config.value; // copy the value onto the main setup.values map
				} else if (this.isUndefined(setup.values[name])) {
					// no value passed on the input or as part of setup.values
					setup.values[name] = null; // set to the default values
				}

				this.assign(config.setup, {
					name,
					container: inputContainer,
					callbacks: {
						focus: (evt) => this.onInputFocus(evt, name),
						blur: (evt) => this.onInputBlur(evt, name),
						update: (value) => this.onInputUpdate(value, name),
						change: (value) => this.onInputChange(value, name),
						error: (value) => this.onInputError(value, name),
						submit: (value) => this.submit(),
					},
				});

				return this.create({
					// create the input
					namespace: `Inputs/${config.type}`,
					setup: config.setup,
					then: function (instance) {
						// input created successfully
						this.inputs[name] = instance; // keep track of the instance
						this.data.groups[name] = config.group; // keep a map of { name : group }

						if (config.disable) {
							instance.disable();
						} else if (config.readonly && this.isFunction(instance.setReadonly)) {
							instance.setReadonly();
						} else if (config.focus === true) {
							inputToFocus = instance;
						}

						return instance; // pass it through for handling below
					},
				});
			});

			return this.when({
				promises: inputPromises,
				then: function (inputs) {
					this.each(setup.values, function (value, key) {
						// iterate over any passed values to prepopulate the form with
						var skipCallbacks = setup.inputs[key].skipCallbacks;

						if (this.isDefined(value)) {
							this.inputs[key].setValue(value, skipCallbacks); // skip callbacks for the initial value population
						}
					});

					if (this.isObject(inputToFocus)) {
						this.defer({
							duration: 100,
							then: inputToFocus.focus,
							done: true,
						});
					}

					this.toggleSubmitButton();
				},
			});
		},

		listeners: [
			{
				event: `click`,
				filter: `[action="submit"]`,
				handler(evt) {
					// listen for any submit button clicks
					this.submit();
				},
			},
			{
				event: `submit`,
				handler: function (evt) {
					evt.preventDefault();
					// this.submit();
				},
			},
		],

		api: {
			/**
			 * The overall handler fired when any of the form's inputs fires a "focus" event.
			 * @method Widgets/Form#onInputFocus
			 * @param {Event} evt - The "focus" event that that fired
			 * @param {string} key - The name of the input it fired on
			 */
			onInputFocus: {
				value(evt, key) {
					if (this.isFunction(this.callbacks.focus)) {
						this.callbacks.focus(evt, key, this.getInput());
					}
				},
			},

			/**
			 * The overall handler fired when any of the form's inputs fires a "blur" event.
			 * @method Widgets/Form#onInputBlur
			 * @param {Event} evt - The "blur" event that that fired
			 * @param {string} key - The name of the input it fired on
			 */
			onInputBlur: {
				value(evt, key) {
					if (this.isFunction(this.callbacks.blur)) {
						this.callbacks.blur(evt, key, this.getInput());
					}
				},
			},

			/**
			 * The overall handler fired when any of the form's inputs fires a "update" event.
			 * @method Widgets/Form#onInputUpdate
			 * @param {Event} evt - The "update" event that that fired
			 * @param {string} key - The name of the input it fired on
			 */
			onInputUpdate: {
				value(value, key) {
					// pass along input update notifications to the instance that created the widget
					if (this.isFunction(this.callbacks.update)) {
						this.callbacks.update(value, key, this.getInput());
					}

					this.toggleSubmitButton();
				},
			},

			/**
			 * The overall handler fired when any of the form's inputs fires a "change" event.
			 * @method Widgets/Form#onInputChange
			 * @param {Event} evt - The "change" event that that fired
			 * @param {string} key - The name of the input it fired on
			 */
			onInputChange: {
				value(value, key) {
					// pass along input change notifications to the instance that created the widget
					if (this.isFunction(this.callbacks.change)) {
						this.callbacks.change(value, key, this.getInput());
					}
				},
			},

			/**
			 * The overall handler fired when any of the form's inputs fires a "error" event.
			 * @method Widgets/Form#onInputError
			 * @param {Event} evt - The "error" event that that fired
			 * @param {string} key - The name of the input it fired on
			 */
			onInputError: {
				value(value, key) {
					// pass along input change notifications to the instance that created the widget
					if (this.isFunction(this.callbacks.error)) {
						this.callbacks.error(value, key, this.getInput());
					}
				},
			},

			/**
			 * The overall handler fired when any of the form's inputs fires a "submit" event.
			 * @method Widgets/Form#onInputSubmit
			 * @param {Event} evt - The "submit" event that that fired
			 * @param {string} key - The name of the input it fired on
			 */
			submit: {
				public: true,
				value(context) {
					// pass along input submit notifications to the instance that created the widget
					var validates = this.validate({
						// check for validation errors on any inputs
						triggerErrors: true,
					});

					if (validates && this.isFunction(this.callbacks.submit)) {
						this.callbacks.submit(this.getValue(), this.getInput()); // pass a map of values and inputs to the callback
					}
				},
			},

			/**
			 * A handler for automatically disabling / enabling any [action="submit"] elements within the form, if applicable
			 * @method Widgets/Form#toggleSubmitButton
			 */
			toggleSubmitButton: {
				value() {
					if (this.data.toggleSubmit) {
						if (
							this.validate({
								triggerErrors: false,
							})
						) {
							this.select(`.button[action="submit"]`).removeClass(`disabled`);
						} else {
							this.select(`.button[action="submit"]`).addClass(`disabled`);
						}
					}
				},
			},

			/**
			 * A function for setting one or more input values at once, mapped by name.
			 * @method Widgets/Form#setValue
			 * @param {Object} options
			 * @param {string|null} options.name=null - Targets a specific Input by name, if passed.
			 * @param {boolean} options.skipCallbacks=false - Whether to avoid firing an onChange callback as a consequence
			 * @param {Object|*} value - The value or map of values to set
			 */
			setValue: {
				public: true,
				value(context, options) {
					// used to set the values of the inputs contained in the form
					options = this.defaults(options, {
						skipCallbacks: false,
						name: null,
					});

					if (this.isString(options.name, 1)) {
						// a specific value is being set
						return this.inputs[options.name].setValue(options.value, options.skipCallbacks); // return the result of the setValue call
					} else {
						// no name specified, assume value is a map of values
						return this.reduce(
							options.value,
							function (result, value, key) {
								result[key] = this.inputs[key].setValue(value, options.skipCallbacks); // set the input value and save the result
								return result; // return the hash object to continue setting values
							},
							{}
						); // start with an empty object that will serve as a hash
					}
				},
			},

			/**
			 * A function for getting one or more input values at once, mapped by name.
			 * @method Widgets/Form#getValue
			 * @param {Object} options
			 * @param {string|null} options.group=null - Targets a specific subset of Inputs by name, if passed
			 * @param {string|null} options.name=null - Targets a specific Input by name, if passed
			 * @returns {*|Object} - The value or map of values from the specified input(s)
			 */
			getValue: {
				public: true,
				value(context, options) {
					options = this.defaults(options, {
						name: null,
						group: null,
					});

					var groups = this.data.groups;

					if (this.isString(options.name)) {
						// a specific value is being requested
						return this.inputs[options.name].getValue();
					} else {
						// no specific input requested, return a hash of everything
						return this.reduce(
							this.inputs,
							function (result, input, key) {
								if (this.isNull(options.group) || groups[input.name] === options.group) {
									result[key] = input.getValue();
								}

								return result;
							},
							{}
						); // start with a blank object to return
					}
				},
			},

			/**
			 * A function for getting one or more input instances, mapped by name.
			 * @method Widgets/Form#getInput
			 * @param {Object} options
			 * @param {string|null} options.group=null - Targets a specific subset of Inputs by name, if passed
			 * @param {string|null} options.name=null - Targets a specific Input by name, if passed
			 * @returns {instance|Object} - The instance or map of instances matching the options passed
			 */
			getInput: {
				public: true,
				value(context, options) {
					options = this.defaults(options, {
						name: null,
						group: null,
					});

					var groups = this.data.groups;

					if (this.isString(options.name, 1)) {
						return this.inputs[options.name];
					} else {
						// return a map of all the inputs
						return this.reduce(
							this.inputs,
							function (result, input, key) {
								if (this.isNull(options.group) || groups[input.name] === options.group) {
									result[key] = input;
								}

								return result;
							},
							{}
						); // start with a blank object to return
					}
				},
			},

			/**
			 * A function for running validate() on one or more Input instances.
			 * @method Widgets/Form#validate
			 * @param {Object} options
			 * @param {string|null} options.group=null - Targets a specific subset of Inputs by name, if passed
			 * @param {string|null} options.name=null - Targets a specific Input by name, if passed
			 * @param {boolean} options.triggerErrors=false - Whether the Input(s) should flag any validation errors
			 * @returns {boolean} - True if all the related validate() calls returned true, otherwise false
			 */
			validate: {
				public: true,
				defaults: {
					triggerErrors: false,
					name: null,
					group: null,
				},
				value(context, options) {
					var focused = false, // tracks whether a "focus on an error" has already occurred
						matching = this.getInput({
							name: options.name,
							group: options.group,
						});

					if (this.isString(options.name, 1)) {
						// only asking about a specific input, narrow the map
						return matching.validate({
							triggerErrors: options.triggerErrors,
						});
					} else {
						// group or all
						return this.reduce(
							matching,
							function (validates, input, name, inputs) {
								var confirm = this.data.confirmations[name]; // check if this input is confirming another's value
								validates =
									input.validate({
										triggerErrors: options.triggerErrors,
									}) && validates; // run validate(), then chain through any existing false

								if (this.isString(confirm, 1)) {
									validates = this.isEqual(input.getValue(), inputs[confirm].getValue()) && validates;

									if (!validates) {
										input.triggerError(); // input can't recognize this internally, manually trigger
									}
								}

								if (options.triggerErrors && !validates) {
									if (!focused) {
										// haven't already focused on something else
										focused = true; // only focus on the first encountered error

										this.App.scrollTo({
											// attempt to scroll the input into view, if not already
											target: input.container,
											duration: 100,
											then: function () {
												input.focus(); // focus into the Textbox, etc.
											},
											done: true,
										});
									}
								}

								return validates;
							},
							true
						); // assume valid to start
					}
				},
			},

			/**
			 * A function for disabling one or more Input instances.
			 * @method Widgets/Form#disable
			 * @param {Object} options
			 * @param {string|null} options.group=null - Targets a specific subset of Inputs by name, if passed
			 * @param {string|null} options.name=null - Targets a specific Input by name, if passed
			 */
			disable: {
				public: true,
				value(context, options) {
					options = this.defaults(options, {
						name: null,
						group: null,
					});

					var matching = this.getInput({
						name: options.name,
						group: options.group,
					});

					if (this.isString(options.name, 1)) {
						// a specific input was requested
						matching.disable();
					} else {
						// a group or all
						this.each(matching, function (input) {
							input.disable();
						});
					}
				},
			},

			/**
			 * A function for enabling one or more Input instances.
			 * @method Widgets/Form#disable
			 * @param {Object} options
			 * @param {string|null} options.group=null - Targets a specific subset of Inputs by name, if passed
			 * @param {string|null} options.name=null - Targets a specific Input by name, if passed
			 */
			enable: {
				public: true,
				value(context, options) {
					options = this.defaults(options, {
						name: null,
						group: null,
					});

					var matching = this.getInput({
						name: options.name,
						group: options.group,
					});

					if (this.isString(options.name, 1)) {
						// a specific input was requested
						matching.enable();
					} else {
						// group or all
						this.each(matching, function (input, key) {
							input.enable();
						});
					}
				},
			},

			/**
			 * A function for updating the Input instances' "rules" configurations.
			 * @method Widgets/Form#updateRules
			 * @param {Object} rulesMap - A mapping of "rules" configurations to pass into the specified inputs' updateRules() calls
			 */
			updateRules: {
				public: true,
				value(context, rulesMap) {
					var inputs = this.getInput();

					this.each(rulesMap, function (rules, inputName) {
						inputs[inputName].updateRules(rules);
					});
				},
			},

			/**
			 * A function for updating the Input instances' "validation" configurations.
			 * @method Widgets/Form#updateValidation
			 * @param {Object} validationMap - A mapping of "validation" configurations to pass into the specified inputs' updateValidation() calls
			 */
			updateValidation: {
				value(validationMap) {
					var inputs = this.getInput();

					this.each(validationMap, function (rules, inputName) {
						inputs[inputName].updateValidation(rules);
					});
				},
			},

			/**
			 * A function for resetting all Input values to null.
			 * @method Widgets/Form#reset
			 * @param {Object} options
			 * @param {boolean} options.skipCallbacks=false - Whether to skip the automatic validate() call after setting input value.
			 */
			reset: {
				public: true,
				defaults: {
					skipCallbacks: false,
				},
				value(context, options) {
					var inputs = this.getInput();

					this.each(inputs, function (input) {
						input.setValue(null, options.skipCallbacks);
						input.onValidateSuccess(true); // clear any errors and reset the firstFocus
					});
				},
			},
		},

		destructor(options) {
			this.defaults(options, {
				transition: `none`,
				remove: false,
			});

			var inputOptions = {
					hide: options.hide,
					transition: options.transition,
					duration: options.duration,
					remove: options.remove,
				},
				promises = [];

			this.each(this.inputs, function (input, name, inputs) {
				promises.push(input.destroy(inputOptions));
			});

			return this.when({
				promises,
			});
		},
	});
});

//# sourceMappingURL=Form.js.map